import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import LandingPage from '@/pages/LandingPage';
import CleanHomePage from '@/pages/CleanHomePage';
import CleanCategoryPage from '@/pages/CleanCategoryPage';
import { AppLayout } from '@/components/AppLayout';
import { setupGlobalErrorHandlers } from '@/utils/globalErrorHandler';

import EnhancedProfilePage from '@/pages/EnhancedProfilePage';
import SessionHistoryPage from '@/pages/SessionHistoryPage';
import CommunitySessionsPage from '@/pages/CommunitySessionsPage';
import GroupsPage from '@/pages/GroupsPage';
import CreateGroupPage from '@/pages/CreateGroupPage';
import GroupDetailPage from '@/pages/GroupDetailPage';
import Auth from '@/pages/Auth';
import { PractitionerDashboard } from '@/pages/PractitionerDashboard';
import { SharedSession } from '@/pages/SharedSession';
import { PractitionerRoute } from '@/components/practitioner/PractitionerRoute';
import ProtectedRoute from '@/components/ProtectedRoute';
import AdminRoute from '@/components/AdminRoute';
import AdminPage from '@/pages/AdminPage';
import Personalization from '@/pages/Personalization';
import Onboarding from '@/pages/Onboarding';
import PreferencesPage from '@/pages/PreferencesPage';
import { PaymentSuccessPage } from '@/pages/PaymentSuccessPage';
import { PaymentCancelPage } from '@/pages/PaymentCancelPage';
import ApplyReferralPage from '@/pages/ApplyReferralPage';
import BiographyLibraryPage from '@/pages/BiographyLibraryPage';
import SendAudioGiftPage from '@/pages/SendAudioGiftPage';
import ReceivedGiftsPage from '@/pages/ReceivedGiftsPage';
import GiftsPage from '@/pages/GiftsPage';
import UserHelpCenterPage from '@/pages/UserHelpCenterPage';
import HelpPage from '@/pages/HelpPage';
import DemoVideosPage from '@/pages/DemoVideosPage';
import PractitionerResourcesPage from '@/pages/PractitionerResourcesPage';

const CleanApp = () => {
  const { user, loading } = useAuth();
  const { isDemoMode } = useDemo();

  // Initialize global error handlers
  useEffect(() => {
    setupGlobalErrorHandlers();
  }, []);

  // Debug logging for routing decisions
  console.log('🔍 Routing Debug:', {
    user: user?.id || 'none',
    isDemoMode,
    isAuthenticated: !!user,
    path: window.location.pathname
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Root Route - Redirect authenticated users to app */}
      <Route 
        path="/" 
        element={(user || isDemoMode) ? <Navigate to="/app" replace /> : <LandingPage />} 
      />
      
      {/* Authentication */}
      <Route
        path="/auth"
        element={(user || isDemoMode) ? <Navigate to="/app" replace /> : <Auth />}
      />
      <Route
        path="/signup"
        element={
          (() => {
            const urlParams = new URLSearchParams(window.location.search);
            const refCode = urlParams.get('ref');

            // If logged in and has referral code, redirect to apply-referral
            if ((user || isDemoMode) && refCode) {
              return <Navigate to={`/apply-referral?ref=${refCode}`} replace />;
            }

            // If logged in without referral code, redirect to app
            if (user || isDemoMode) {
              return <Navigate to="/app" replace />;
            }

            // Not logged in, show auth page
            return <Auth />;
          })()
        }
      />

      {/* Apply Referral Code - Protected route for logged-in users */}
      <Route
        path="/apply-referral"
        element={
          <ProtectedRoute>
            <ApplyReferralPage />
          </ProtectedRoute>
        }
      />

      {/* App Routes - Allow both authenticated and demo users */}
      <Route path="/app" element={
        <AppLayout>
          <CleanHomePage />
        </AppLayout>
      } />
      <Route path="/category/:categoryId" element={
        <AppLayout>
          <CleanCategoryPage />
        </AppLayout>
      } />
      <Route path="/community" element={
        <AppLayout>
          <CommunitySessionsPage />
        </AppLayout>
      } />

      {/* Help Pages - Available to all users */}
      <Route path="/help" element={<HelpPage />} />
      <Route path="/help-center" element={<UserHelpCenterPage />} />
      <Route path="/demo-videos" element={<DemoVideosPage />} />

      {/* Onboarding Flow - Available to all users */}
      <Route path="/personalization" element={<Personalization />} />
      <Route path="/onboarding" element={<Onboarding />} />

      {/* Payment Routes - Protected */}
      <Route path="/payment/success" element={
        <ProtectedRoute>
          <PaymentSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/payment/cancel" element={
        <ProtectedRoute>
          <PaymentCancelPage />
        </ProtectedRoute>
      } />

      {/* Protected Profile Routes - Only for authenticated users */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <AppLayout>
            <EnhancedProfilePage />
          </AppLayout>
        </ProtectedRoute>
      } />
      {/* Redirect old biography route to profile */}
      <Route path="/biography" element={<Navigate to="/profile" replace />} />
      <Route path="/sessions/history" element={
        <ProtectedRoute>
          <AppLayout>
            <SessionHistoryPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/preferences" element={
        <ProtectedRoute>
          <AppLayout>
            <PreferencesPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/biographies" element={
        <ProtectedRoute>
          <AppLayout>
            <BiographyLibraryPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      {/* Unified gifts hub */}
      <Route path="/gifts" element={
        <ProtectedRoute>
          <AppLayout>
            <GiftsPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      {/* Redirects for old gift URLs */}
      <Route path="/send-gift" element={<Navigate to="/gifts?tab=send" replace />} />
      <Route path="/received-gifts" element={<Navigate to="/gifts?tab=received" replace />} />
      <Route path="/groups" element={
        <ProtectedRoute>
          <AppLayout>
            <GroupsPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/groups/new" element={
        <ProtectedRoute>
          <AppLayout>
            <CreateGroupPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/groups/:groupId" element={
        <ProtectedRoute>
          <AppLayout>
            <GroupDetailPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/practitioner/resources" element={
        <ProtectedRoute>
          <PractitionerRoute>
            <PractitionerResourcesPage />
          </PractitionerRoute>
        </ProtectedRoute>
      } />
      <Route path="/practitioner/*" element={
        <ProtectedRoute>
          <PractitionerRoute>
            <PractitionerDashboard />
          </PractitionerRoute>
        </ProtectedRoute>
      } />
      <Route path="/admin/*" element={
        <ProtectedRoute>
          <AdminRoute>
            <AppLayout>
              <AdminPage />
            </AppLayout>
          </AdminRoute>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={<Navigate to="/preferences" replace />} />
      <Route path="/listen/:accessToken" element={<SharedSession />} />

      {/* Fallback - Redirect unmatched routes */}
      <Route path="*" element={<Navigate to={(user || isDemoMode) ? "/app" : "/"} replace />} />
    </Routes>
  );
};

export default CleanApp;